import ActionButton from "./ActionButton";
import ActionButtonGroup, { ActionButtonConfig } from "./ActionButtonGroup";
import {
  Add,
  Delete,
  Edit,
  Search
  } from "@mui/icons-material";
import { ApplicationStatus } from "../../interfaces/enums";
import { Box, Tooltip } from "@mui/material";

interface ActionButtonsProps {
  hasApplication: boolean;
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
  applicationStatus?: ApplicationStatus;
  disabled?: boolean;
  disabledReason?: string;
  hideWhenDisabled?: boolean;
}

export default function ActionButtons({
  hasApplication,
  onAdd,
  onEdit,
  onDelete,
  applicationStatus,
  disabled = false,
  disabledReason,
  hideWhenDisabled = false,
}: ActionButtonsProps) {
  // Jeśli hideWhenDisabled i disabled, nie renderuj przycisków tylko gdy nie ma aplikacji
  // Gdy jest aplikacja, przycisk "Edytuj/Pokaż zgłoszenie" jest zawsze widoczny
  if (hideWhenDisabled && disabled && !hasApplication) {
    return null;
  }
  const buttonStyle = {
    minWidth: "160px",
    py: 1.0,
    px: 1.5,
    fontSize: "0.75rem",
  };

  // Określ nazwę i ikonę przycisku w zależności od statusu
  const getEditButtonLabel = () => {
    // Jeśli rejestracja zamknięta (disabled === true), zawsze pokazujemy „Pokaż zgłoszenie”
    if (disabled) {
      return "Pokaż zgłoszenie";
    }
    if (applicationStatus === ApplicationStatus.DRAFT) {
      return "Edytuj zgłoszenie";
    }
    return "Pokaż zgłoszenie";
  };

  const getEditButtonIcon = () => {
    // Jeśli rejestracja zamknięta (disabled === true), używamy ikony „Search”
    if (disabled) {
      return <Search />;
    }
    if (applicationStatus === ApplicationStatus.DRAFT) {
      return <Edit />;
    }
    return <Search />;
  };

  // Sprawdź czy zgłoszenie może być usunięte na podstawie statusu
  const canDeleteApplication = () => {
    if (!applicationStatus) return true; // Jeśli brak statusu, pozwól (dla bezpieczeństwa)
    
    // Zgłoszenie nie może być usunięte w tych statusach:
    const blockedStatuses = [
      ApplicationStatus.APPROVED,
      ApplicationStatus.REJECTED,
      ApplicationStatus.MODERATION,
      ApplicationStatus.SENT,
      ApplicationStatus.CANCELED,
    ];
    
    return !blockedStatuses.includes(applicationStatus);
  };

  const getButtons = (): ActionButtonConfig[] => {
    if (hasApplication) {
      const buttons: ActionButtonConfig[] = [];

      // Przycisk edycji/podglądu - zawsze dostępny gdy jest aplikacja
      // Zmienia nazwę w zależności od statusu:
      // - DRAFT → "Edytuj zgłoszenie"
      // - Inne → "Pokaż zgłoszenie"
      buttons.push({
        id: "edit",
        label: getEditButtonLabel(),
        icon: getEditButtonIcon(),
        variant: "outlined",
        onClick: onEdit,
        disabled: false, // Zawsze dostępny (nawet gdy rejestracja zamknięta, działa jako "Pokaż zgłoszenie")
        sx: {
          ...buttonStyle,
          color: "#6366f1",
          borderColor: "#6366f1",
          "&:hover": {
            backgroundColor: "#f1e7de",
            borderColor: "#6366f1",
          },
        },
      });

      // Przycisk usuwania - pokazuj tylko gdy status pozwala na usunięcie
      // Jeśli status nie pozwala na usunięcie, przycisk jest całkowicie ukryty
      if (canDeleteApplication()) {
        // Sprawdź czy przycisk powinien być wyłączony (rejestracja zamknięta)
        const isDeleteDisabled = hideWhenDisabled && disabled;
        
        buttons.push({
          id: "delete",
          label: "Usuń zgłoszenie",
          icon: <Delete />,
          variant: "outlined",
          color: "error",
          onClick: onDelete,
          disabled: isDeleteDisabled,
          sx: {
            ...buttonStyle,
            color: "#ef4444",
            borderColor: "#ef4444",
            "&:hover": {
              backgroundColor: "#fef2f2",
              borderColor: "#ef4444",
            },
          },
        });
      }

      return buttons;
    }

    return [
      {
        id: "add",
        label: "Dodaj zgłoszenie",
        icon: <Add />,
        variant: "contained",
        disabled,
        onClick: onAdd,
        sx: {
          ...buttonStyle,
          backgroundColor: "#10b981",
          "&:hover": { backgroundColor: "#059669" },
          "&:disabled": { backgroundColor: "#9ca3af" },
        },
      },
    ];
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "center",
        height: "100%",
      }}
    >
      {hasApplication ? (
        <ActionButtonGroup
          buttons={getButtons()}
          spacing={1}
          justifyContent="center"
          sx={{ flexDirection: "column" }}
        />
      ) : (
        <Tooltip title={disabled && disabledReason ? disabledReason : ""} arrow>
          <span>
            <ActionButton
              icon={<Add />}
              variant="contained"
              disabled={disabled}
              onClick={onAdd}
              sx={{
                ...buttonStyle,
                backgroundColor: "#10b981",
                "&:hover": { backgroundColor: "#059669" },
                "&:disabled": { backgroundColor: "#9ca3af" },
              }}
            >
              Dodaj zgłoszenie
            </ActionButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
